// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { LogOut, Lock, ChevronDown, ChevronRight } from 'lucide-react'
import { useAuth, portalBrandLabel } from '../auth/AuthContext'
import { useModules } from '../auth/ModulesContext'
import { usePharmacy } from '../auth/PharmacyContext'
import { navConfig, visibleNav, NavEntry, routeMatches } from './navConfig'
import * as api from '../api/client'
import { SidebarMode } from './useSidebarState'

interface Props {
  mode: SidebarMode
  collapsed: boolean        // desktop only
  overlayOpen: boolean      // tablet/mobile only
  closeOverlay: () => void
}

/**
 * The left rail. Looks different in three modes:
 *
 *  - desktop, expanded:  240px, labels visible
 *  - desktop, collapsed: 64px, icons only with tooltips
 *  - tablet/mobile, overlay open:  full-height overlay with backdrop
 *  - tablet/mobile, overlay closed: not rendered
 */
export default function Sidebar({ mode, collapsed, overlayOpen, closeOverlay }: Props) {
  const { me, role, signOut } = useAuth()
  const { mode: uiMode, activePharmacy } = usePharmacy()
  const { isDisabled: isModuleDisabled } = useModules()
  const location = useLocation()
  const navigate = useNavigate()

  // Auto-close overlay on route change
  useEffect(() => {
    if (mode !== 'desktop' && overlayOpen) closeOverlay()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  const isOverlay = mode !== 'desktop'
  const isHidden  = isOverlay && !overlayOpen

  // D-SUPPORT-3: live open/my-open counts for the Support queue + My queue nav badges.
  // Polled on the same cadence class as notifications; only for platform accounts that
  // can actually see the queue, so pharmacy sessions never hit the endpoint.
  const canSeeQueue = Boolean(me?.authenticated) && !me?.pharmacy_id && (me?.permissions ?? []).includes('support.queue.read')
  const [supportBadges, setSupportBadges] = useState<{ open: number; mine: number } | null>(null)
  useEffect(() => {
    if (!canSeeQueue) { setSupportBadges(null); return }
    let stop = false
    const pull = () => api.getSupportBadges().then(b => { if (!stop) setSupportBadges(b) }).catch(() => {})
    pull()
    const t = setInterval(pull, 60_000)
    return () => { stop = true; clearInterval(t) }
  }, [canSeeQueue])

  if (isHidden) return null

  // D-MFR-1 — an external manufacturer has no pharmacy navigation. Narrowing to null
  // at this boundary (rather than widening navConfig's Role) is the point: the pharmacy
  // nav config stays a closed set of internal roles, and an external party can never be
  // routed through it even if this component is mounted by mistake.
  // D-SOC-9 S1: 'facility' passes through as ITSELF and gets its own nav entries
  // (facility-home / facility-rx / facility-residents). This line used to rewrite it to
  // 'provider' under D-SOC-3 — the last remnant of that design after #2713 took facility off
  // the /provider routes. The rewrite is why a facility user saw "My patients -> /provider/
  // patients" in the sidebar: links into a portal RequireRole now refuses them.
  const navRole = role === 'manufacturer' ? null : role

  const entries = visibleNav(navConfig, {
    role: navRole,
    permissions: me?.permissions ?? [],
    authenticated: Boolean(me?.authenticated),
    mode: uiMode,
    isModuleDisabled,
  })

  const isIcon = mode === 'desktop' && collapsed
  // A `?tab=` child (CareRx admin) claims the page: its plain parent route (/carerx) is then not
  // also highlighted — one active row, the one the user clicked.
  const claimedByChild = entries.some(e => e.kind !== 'divider' && e.children?.some(c => routeMatches(c, location.pathname, location.search)))

  async function handleSignOut() {
    await signOut()
    navigate('/login')  // basename prefixes to /p/{slug}/login for a pharmacy session
  }

  const sidebarStyle: React.CSSProperties = isOverlay
    ? overlayStyles
    : (collapsed ? collapsedStyles : expandedStyles)

  return (
    <>
      {/* Backdrop (mobile/tablet overlay only) */}
      {isOverlay && (
        <div onClick={closeOverlay} style={backdropStyle} aria-hidden="true" />
      )}

      <nav style={sidebarStyle} aria-label="Main">
        {/* Brand row */}
        <div style={brandRowStyle}>
          {activePharmacy?.logo_url ? (
            <img src={activePharmacy.logo_url} alt="" aria-hidden="true"
                 style={{ ...logoStyle, objectFit: 'contain', background: '#fff', padding: 2 }} />
          ) : (
            <span style={logoStyle} aria-hidden="true">O</span>
          )}
          {!isIcon && (
            <>
              <span style={brandTextStyle}>{portalBrandLabel(role)}</span>
            </>
          )}
        </div>

        {/* Nav list */}
        <div style={{ flex: 1, overflowY: 'auto', paddingTop: 8 }}>
          {entries.map(entry => (
            entry.kind === 'divider'
              ? <DividerRow key={entry.key} label={entry.label} hidden={isIcon} />
              : entry.children
              ? <NavGroup key={entry.key} entry={entry} iconOnly={isIcon} pathname={location.pathname} search={location.search}
                  locked={!!(entry.requiredModule && isModuleDisabled(entry.requiredModule))} />
              : <NavRow
                  suppressActive={claimedByChild && !entry.route?.includes('?')}
                  key={entry.key}
                  entry={entry}
                  iconOnly={isIcon}
                  locked={!!(entry.requiredModule && isModuleDisabled(entry.requiredModule))}
                  badgeCount={entry.key === 'notifications' ? (me?.unread_notifications ?? 0)
                    : entry.key === 'saas-support' ? (supportBadges?.open ?? 0)
                    : entry.key === 'saas-support-mine' ? (supportBadges?.mine ?? 0)
                    : 0}
                />
          ))}
          {(role === 'provider' || role === 'provider_nurse') && <ProviderNavExtras iconOnly={isIcon} />}
        </div>

        {/* User badge in footer */}
        {me?.authenticated && (
          <div style={{ ...userBadgeStyle, justifyContent: isIcon ? 'center' : 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
              <div style={avatarStyle} aria-hidden="true">
                {(me.display_name ?? '?').charAt(0).toUpperCase()}
              </div>
              {!isIcon && (
                <div style={{ minWidth: 0 }}>
                  <div style={userNameStyle}>{me.display_name ?? me.email}</div>
                  {role && (
                    <div style={userRoleStyle}>
                      {me.role_name
                       ?? (role === 'admin' ? 'Administrator'
                       : role === 'pharmacy_staff' ? 'Pharmacy'
                       : role === 'provider' ? 'Provider'
                       : 'Patient')}
                    </div>
                  )}
                </div>
              )}
            </div>
            {!isIcon && (
              <button
                onClick={handleSignOut}
                style={signOutBtnStyle}
                aria-label="Sign out"
                title="Sign out"
              >
                <LogOut size={15} aria-hidden="true" />
              </button>
            )}
          </div>
        )}
      </nav>
    </>
  )
}

// ----------------------------------------------------------------------
// Sub-components
// ----------------------------------------------------------------------

function DividerRow({ label, hidden }: { label?: string; hidden: boolean }) {
  if (hidden) return <div style={hiddenDividerStyle} aria-hidden="true" />
  return (
    <div style={dividerRowStyle} role="separator">
      {label && <span style={dividerLabelStyle}>{label.toUpperCase()}</span>}
    </div>
  )
}

/** A parent with sub-items (one level). Opens on its own when one of its items is the current page. */
function NavGroup({ entry, iconOnly, pathname, search, locked }: {
  entry: Extract<NavEntry, { kind?: 'item' | undefined }>
  iconOnly: boolean
  pathname: string
  search: string
  locked?: boolean
}) {
  const kids = entry.children ?? []
  const current = kids.some(c => routeMatches(c, pathname, search))
  const [open, setOpen] = useState(current)
  useEffect(() => { if (current) setOpen(true) }, [current])
  const Icon = entry.icon
  const listId = `nav-group-${entry.key}`
  return (
    <div>
      {!iconOnly && (
        <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} aria-controls={listId}
          style={{ fontFamily: 'inherit', ...navRowStyle, width: '100%', borderTop: 'none', borderRight: 'none', borderBottom: 'none',
            background: 'transparent', cursor: 'pointer', textAlign: 'left', ...(locked ? { opacity: 0.55 } : null) }}>
          <Icon size={18} aria-hidden="true" />
          <span style={{ flex: 1 }}>{entry.label}</span>
          {open ? <ChevronDown size={14} aria-hidden="true" /> : <ChevronRight size={14} aria-hidden="true" />}
        </button>
      )}
      {(open || iconOnly) && (
        <div id={listId} style={iconOnly ? undefined : { paddingLeft: 14 }}>
          {kids.map(c => (
            <NavRow key={c.key} entry={c} iconOnly={iconOnly} locked={locked}
              activeOverride={routeMatches(c, pathname, search)} />
          ))}
        </div>
      )}
    </div>
  )
}

function NavRow({ entry, iconOnly, badgeCount, locked, suppressActive, activeOverride }: {
  entry: Extract<NavEntry, { kind?: 'item' | undefined }>
  iconOnly: boolean
  badgeCount?: number
  locked?: boolean
  /** The page belongs to a `?tab=` sub-item — do not also highlight this plain route. */
  suppressActive?: boolean
  /** NavLink matches the path only; a `?tab=` item decides its own active state. */
  activeOverride?: boolean
}) {
  if (!('route' in entry) || !entry.route) return null
  const Icon = entry.icon
  const showBadge = (badgeCount ?? 0) > 0
  const lockTip = 'Not included in your plan — contact your account executive for licensing'
  return (
    <NavLink
      to={entry.route}
      end={entry.route === '/'}
      // D-PERF-3: pointer entering the Prescriptions item = imminent navigation — pre-warm its list
      // cache (throttled, registered-paths-only; no-op for every other nav item and when disabled).
      onPointerEnter={entry.key === 'rx' && !locked ? () => api.prefetchRxQueueOnIntent() : undefined}
      title={locked ? `${entry.label} — ${lockTip}` : (iconOnly ? `${entry.label}${showBadge ? ` (${badgeCount} unread)` : ''}` : undefined)}
      // NavLink sets aria-current and the `active` class from the PATH alone — both follow the effective state.
      aria-current={activeOverride === undefined && !suppressActive ? undefined : (activeOverride ? 'page' : 'false')}
      className={({ isActive: pathActive }) => ((activeOverride ?? (pathActive && !suppressActive)) ? 'active' : '')}
      style={({ isActive: pathActive }) => {
        const isActive = activeOverride ?? (pathActive && !suppressActive)
        return ({
        ...navRowStyle,
        ...(isActive && !locked ? navRowActiveStyle : null),
        ...(iconOnly ? navRowIconOnlyStyle : null),
        ...(locked ? { opacity: 0.55 } : null),
        position: 'relative',
      }) }}
    >
      <span style={{ position: 'relative', display: 'inline-flex' }}>
        <Icon size={18} aria-hidden="true" />
        {showBadge && iconOnly && (
          <span style={iconBadgeDotStyle} aria-hidden="true" />
        )}
      </span>
      {!iconOnly && <span style={{ flex: 1 }}>{entry.label}</span>}
      {!iconOnly && locked && <Lock size={13} aria-hidden="true" style={{ color: 'var(--color-text-soft)' }} />}
      {!iconOnly && !locked && showBadge && (
        <span style={badgeStyle} aria-label={`${badgeCount} unread`}>
          {badgeCount! > 99 ? '99+' : badgeCount}
        </span>
      )}
    </NavLink>
  )
}

// ----------------------------------------------------------------------
// Styles
// ----------------------------------------------------------------------

const sharedSidebarBase: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  background: '#fafaf9',
  borderRight: '1px solid var(--color-border)',
  height: '100vh',
  position: 'sticky',
  top: 0,
  flexShrink: 0,
  fontFamily: 'var(--font-sans, system-ui)',
}

const expandedStyles: React.CSSProperties = {
  ...sharedSidebarBase,
  width: 240,
}

const collapsedStyles: React.CSSProperties = {
  ...sharedSidebarBase,
  width: 64,
}

const overlayStyles: React.CSSProperties = {
  ...sharedSidebarBase,
  width: 280,
  position: 'fixed',
  top: 0, left: 0,
  zIndex: 40,
  boxShadow: 'var(--shadow-lg, 0 8px 24px rgba(0,0,0,0.08))',
}

const backdropStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.4)',
  zIndex: 39,
}

const brandRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '14px 14px',
  borderBottom: '1px solid var(--color-border)',
  minHeight: 56,
}

const logoStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center', justifyContent: 'center',
  width: 32, height: 32,
  background: 'var(--color-primary)',
  color: '#fff',
  borderRadius: 'var(--radius-sm, 6px)',
  fontWeight: 700, fontSize: 16,
  flexShrink: 0,
}

const brandTextStyle: React.CSSProperties = {
  fontWeight: 600, fontSize: 15,
  color: 'var(--color-text)',
}

const navRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '8px 14px',
  fontSize: 13.5,
  color: 'var(--color-text-soft)',
  textDecoration: 'none',
  // Longhands, not the `borderLeft` shorthand: when a row stops being active React REMOVES the active
  // style's borderLeftColor, and with a shorthand that also wipes its transparent colour — the row kept
  // a dark bar (currentColor) after it was left.
  borderLeftWidth: 3,
  borderLeftStyle: 'solid',
  borderLeftColor: 'transparent',
}

const navRowActiveStyle: React.CSSProperties = {
  color: 'var(--color-text)',
  background: 'var(--color-primary-soft)',
  borderLeftColor: 'var(--color-primary)',
  fontWeight: 600,
}

const navRowIconOnlyStyle: React.CSSProperties = {
  justifyContent: 'center',
  paddingLeft: 8, paddingRight: 8,
}

const dividerRowStyle: React.CSSProperties = {
  padding: '14px 14px 4px',
}

const dividerLabelStyle: React.CSSProperties = {
  fontSize: 10, fontWeight: 700, letterSpacing: 1,
  color: 'var(--color-text-mute)',
}

const hiddenDividerStyle: React.CSSProperties = {
  height: 8,
  borderTop: '1px solid var(--color-border)',
  margin: '4px 12px',
}

const userBadgeStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  padding: 12,
  borderTop: '1px solid var(--color-border)',
  gap: 10,
}

const avatarStyle: React.CSSProperties = {
  width: 36, height: 36, flexShrink: 0,
  background: 'var(--color-primary)',
  color: '#fff',
  borderRadius: '50%',
  display: 'inline-flex',
  alignItems: 'center', justifyContent: 'center',
  fontWeight: 600, fontSize: 14,
}

const userNameStyle: React.CSSProperties = {
  fontSize: 13, fontWeight: 600,
  color: 'var(--color-text)',
  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  maxWidth: 130,
}

const userRoleStyle: React.CSSProperties = {
  fontSize: 11,
  color: 'var(--color-text-mute)',
  marginTop: 1,
}

const signOutBtnStyle: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  padding: 8,
  borderRadius: 'var(--radius-sm, 6px)',
  cursor: 'pointer',
  color: 'var(--color-text-mute)',
}

const badgeStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 20,
  height: 18,
  padding: '0 6px',
  fontSize: 10,
  fontWeight: 700,
  lineHeight: 1,
  background: '#dc2626',
  color: '#fff',
  borderRadius: 9,
  marginLeft: 'auto',
}

const iconBadgeDotStyle: React.CSSProperties = {
  position: 'absolute',
  top: -2,
  right: -4,
  width: 8,
  height: 8,
  background: '#dc2626',
  border: '2px solid #fafaf9',
  borderRadius: '50%',
}


// I-988: provider/nurse care-team rail rendered under "My prescriptions": a link to
// "My patients" plus the linked accounts (nurses a provider granted access to, or the
// providers a nurse can see), by name. Read-only; populated from GET /provider/links.
function ProviderNavExtras({ iconOnly }: { iconOnly: boolean }) {
  const [links, setLinks] = useState<api.ProviderLink[]>([])
  useEffect(() => { let live = true; api.listProviderLinks().then(r => { if (live) setLinks(r.links) }).catch(() => { if (live) setLinks([]) }); return () => { live = false } }, [])
  if (iconOnly) return null
  if (links.length === 0) return null
  return (
    <div style={extrasWrap}>
      <div style={extraHeading}>Care team</div>
      {links.map(l => (
        <div key={l.id} style={extraName} title={`${l.name ?? 'Unknown'} · ${l.role}`}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.name ?? 'Unknown'}</span>
          <span style={{ color: 'var(--color-text-mute)', fontSize: 11, flexShrink: 0 }}>· {l.role}</span>
        </div>
      ))}
    </div>
  )
}

const extrasWrap: React.CSSProperties = { padding: '2px 8px 10px 28px', display: 'flex', flexDirection: 'column', gap: 2 }
const extraHeading: React.CSSProperties = { fontSize: 10, textTransform: 'uppercase', letterSpacing: '.5px', color: 'var(--color-text-mute)', margin: '8px 8px 2px' }
const extraName: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px', fontSize: 12.5, color: 'var(--color-text)' }
